import { Directive, ElementRef, Input, NgZone, OnChanges, OnDestroy } from '@angular/core';

const LOCK_SELECTOR = [
  'input',
  'textarea',
  'select',
  'button',
  '.three-dots',
  'p-select',
  'p-dropdown',
  'p-calendar',
  'p-datepicker',
  'p-checkbox',
  'p-radiobutton',
  'p-tableradiobutton',
  'p-inputnumber',
  'p-inputswitch',
  'p-toggleswitch',
  'p-editor',
  'p-autocomplete',
  'p-fileupload',
  '.p-selectable-row',
  '.p-editable-column',
  '[contenteditable="true"]',
].join(',');

// Areas that only read data (sorting, paging, column chooser, documents list) stay usable.
// Dialogs and toasts are also exempt so warnings and alerts can always be closed (OK / X). The actions that
// open an editing dialog are locked, so this does not let a view-only role make changes.
const EXEMPT_SELECTOR = [
  'view-file',
  'p-paginator',
  'p-multiselect',
  '.p-datatable-header',
  '.p-datatable-thead',
  'thead',
  'p-dialog',
  '.p-dialog',
  'p-confirmdialog',
  '.p-confirm-dialog',
  'p-toast',
  '.p-toast',
].join(',');

interface ILockedState {
  disabled?: boolean;
  pointerEvents: string;
}

/**
 * Makes everything inside the host read-only for view-only roles:
 * form controls are disabled, and row menus / selectors stop reacting to clicks.
 * Elements that only read data (see EXEMPT_SELECTOR) are left alone.
 */
@Directive({
  standalone: false,
  selector: '[appViewOnlyLock]',
})
export class ViewOnlyLockDirective implements OnChanges, OnDestroy {
  @Input('appViewOnlyLock') locked = false;

  private observer?: MutationObserver;
  private frame?: number;
  private readonly lockedElements = new Map<HTMLElement, ILockedState>();

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private zone: NgZone,
  ) {}

  ngOnChanges(): void {
    if (this.locked) {
      this.start();
    } else {
      this.stop();
    }
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private start(): void {
    if (this.observer) {
      return;
    }
    this.zone.runOutsideAngular(() => {
      this.applyLock();
      this.observer = new MutationObserver(() => this.scheduleApply());
      this.observer.observe(this.elementRef.nativeElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled'],
      });
    });
  }

  private stop(): void {
    this.observer?.disconnect();
    this.observer = undefined;
    if (this.frame !== undefined) {
      cancelAnimationFrame(this.frame);
      this.frame = undefined;
    }
    this.lockedElements.forEach((state, element) => {
      element.style.pointerEvents = state.pointerEvents;
      if (state.disabled !== undefined) {
        (element as HTMLInputElement).disabled = state.disabled;
      }
    });
    this.lockedElements.clear();
  }

  private scheduleApply(): void {
    if (this.frame !== undefined) {
      return;
    }
    this.frame = requestAnimationFrame(() => {
      this.frame = undefined;
      this.applyLock();
    });
  }

  private applyLock(): void {
    const host = this.elementRef.nativeElement;
    host.querySelectorAll<HTMLElement>(LOCK_SELECTOR).forEach((element) => {
      if (element.closest(EXEMPT_SELECTOR)) {
        return;
      }
      if (!this.lockedElements.has(element)) {
        this.lockedElements.set(element, {
          disabled: 'disabled' in element ? (element as HTMLInputElement).disabled : undefined,
          pointerEvents: element.style.pointerEvents,
        });
      }
      if ('disabled' in element && !(element as HTMLInputElement).disabled) {
        (element as HTMLInputElement).disabled = true;
      }
      if (element.style.pointerEvents !== 'none') {
        element.style.pointerEvents = 'none';
      }
    });
  }
}
